// @ts-nocheck
import { isEmpty, uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getLogo, getLogoAdvertiser, postLogo, updateLogoStatus } from '@/modules/reporting/reporting.resources';

import './styles.scss';

import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';

import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

const getId = (value: string) => `reporting.header.single.${value}`;

type Props = {
  data: CampaignItem[];
  src?: string;
};

type State = {
  image: {
    hidden?: boolean;
    preview?: string;
    raw?: Record<string, any>;
    src?: string;
  };
  srcAdvertiser?: string;
  status: string;
};

function Logo({ data, src }: Props) {
  const { formatMessage } = useIntl();

  const userRoles = useSelector(getUserRoles);
  const user = useSelector(getCurrentUser);

  const campaignIds = data.map(d => d.id);

  const [{ image, srcAdvertiser, status }, setState] = useState<State>({
    image: {
      hidden: false
    },
    status: 'idle'
  });
  const baseImageURL = 'https://cdnsonata.taptapnetworks.com';
  const eyeIcon = image.hidden ? 'fal fa-eye-slash' : 'fal fa-eye';
  const isHover = status === 'hover';
  const uploadedLogo = image.src || srcAdvertiser || src;

  const getMediaAdvertiserId = () => {
    let mediaAdvertiserId;
    if (data.length > 1) {
      const uniqueAdvertisersIds = uniq(data.map(d => d.mediaAdvertiser._id));
      mediaAdvertiserId = uniqueAdvertisersIds.length > 1 ? null : uniqueAdvertisersIds;
    } else {
      mediaAdvertiserId = data[0].mediaAdvertiser._id;
    }
    return mediaAdvertiserId;
  };

  const getLogoMediaAdvertiser = async () => {
    const mediaAdvertiserId = getMediaAdvertiserId();
    if (mediaAdvertiserId) {
      const mediaAdvertiserLogo = await getLogoAdvertiser(user.id, mediaAdvertiserId);
      if (!isEmpty(mediaAdvertiserLogo)) {
        setState(prevState => ({
          ...prevState,
          srcAdvertiser: `${baseImageURL}${mediaAdvertiserLogo.logo}`
        }));
      }
    }
  };

  const getLogoFromCampaign = async () => {
    const result = await getLogo(user.id, campaignIds);
    if (!isEmpty(result)) {
      const { deleted, image: logoImage, visible } = result;
      setState(prevState => ({
        ...prevState,
        image: {
          hidden: !visible,
          preview: undefined,
          raw: undefined,
          src: deleted ? undefined : `${baseImageURL}${logoImage}`
        },
        status: 'idle'
      }));
    }
  };

  useEffect(() => {
    getLogoMediaAdvertiser();
    getLogoFromCampaign();
  }, []);

  const onInputChange = (e: any) => {
    if (e.target.files.length) {
      const preview = URL.createObjectURL(e.target.files[0]);
      const raw = e.target.files[0];
      setState(prevState => ({
        ...prevState,
        image: {
          preview,
          raw,
          src: ''
        }
      }));
    }
  };

  const uploadLogoToCampaign = async () => {
    if (image.raw) {
      try {
        const result = await postLogo(user.id, campaignIds, image.raw, image.raw.name);
        setState(prevState => ({
          ...prevState,
          image: {
            preview: undefined,
            raw: undefined,
            src: `${baseImageURL}${result.url}`
          },
          status: 'idle'
        }));
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: off
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (!image.src) {
      uploadLogoToCampaign();
    }
  }, [image]);

  const onHoverChange = (value: string) => {
    setState(prevState => ({
      ...prevState,
      status: value
    }));
  };

  const onUpdateLogoStatus = async (type: string) => {
    const method = type === 'delete' ? 'DELETE' : 'PUT';
    const isHidden = image.hidden;

    try {
      const { deleted, visible } = await updateLogoStatus(user.id, campaignIds, method, isHidden);

      setState(prevState => ({
        ...prevState,
        image: {
          hidden: !visible,
          preview: undefined,
          raw: undefined,
          src: deleted ? undefined : prevState.image.src
        }
      }));
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(e);
    }
  };

  const handleOnHoverChange = (value: string) => {
    if (uploadedLogo) {
      switch (value) {
        case 'hover':
          return () => onHoverChange('hover');
        case 'idle':
          return () => onHoverChange('idle');
        default:
          return null;
      }
    }
  };

  const manageLogoPermissions = checkUserResourcePermissions('reportingManageLogo', user, userRoles);

  return (
    <div className="logo">
      <div
        className={`logo__container ${image.hidden ? 'logo__container--hidden' : ''}`.trim()}
        // @ts-ignore
        onMouseEnter={handleOnHoverChange('hover')}
      >
        {uploadedLogo ? (
          <>
            <img
              alt="campaign"
              className={`logo__image ${image.hidden ? 'logo__image--hidden' : ''}`.trim()}
              src={uploadedLogo}
            />
            {manageLogoPermissions && image.src && (
              // biome-ignore lint/a11y/useButtonType: off
              <button className="logo__imageDelete" onClick={() => onUpdateLogoStatus('delete')}>
                <i className="fa fa-times logo__imageDeleteIcon" />
              </button>
            )}
          </>
        ) : null}
        {image.hidden ? (
          <span className="logo__imageHiddenText">{formatMessage({ id: getId('logo.hidden') })}</span>
        ) : null}
      </div>
      {manageLogoPermissions && (isHover || !uploadedLogo) ? (
        // @ts-ignore
        <div className="logo__upload" onMouseLeave={handleOnHoverChange('idle')}>
          <label htmlFor="upload-logo">
            <ButtonUI
              className="logo__uploadButton"
              icon="far fa-arrow-to-top"
              iconPosition="left"
              id="upload_logo_reporting_header_button"
              shape="link"
              size="small"
              text={formatMessage({ id: getId('logo.upload') })}
              type="secondary"
            />
          </label>
        </div>
      ) : null}
      <input id="upload-logo" name="upload-logo" onChange={onInputChange} style={{ display: 'none' }} type="file" />
      {uploadedLogo && manageLogoPermissions ? (
        // biome-ignore lint/a11y/useButtonType: off
        <button
          className={`logo__visible ${isHover ? 'logo__visible--disabled' : ''}`.trim()}
          onClick={() => onUpdateLogoStatus('put')}
        >
          <i className={`${eyeIcon} ${isHover ? 'logo__visible-icon--disabled' : ''}`.trim()} />
        </button>
      ) : null}
    </div>
  );
}

export default Logo;
